import React from 'react';
import { createRoot } from 'react-dom/client';
import { ChatWidget } from './ChatWidget';
import type { ChatWidgetConfig, ChatWidgetInstance, ChatStorageData } from './types';

// Import styles
import '../../../shared/styles/index.css';
import './styles.css';

// Storage key - must match ChatWidget.tsx
const STORAGE_KEY = 'pospeh-chat-widget';

// Global API namespace
declare global {
  interface Window {
    PospehWidgets: {
      Chat: {
        init: (config: ChatWidgetConfig) => ChatWidgetInstance;
      };
    };
  }
}

// Initialize global namespace
if (typeof window !== 'undefined') {
  window.PospehWidgets = window.PospehWidgets || {};
}

// Helper function to check if there's an active (non-expired) chat session in localStorage
const hasActiveSession = (config: ChatWidgetConfig): boolean => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return false;

    const parsed: ChatStorageData = JSON.parse(data);

    // Check if session has expired
    const lastMessageAt = new Date(parsed.lastMessageAt);
    const expirationMinutes = parsed.expirationMinutes || config.sessionExpirationMinutes || 60;
    const expirationMs = expirationMinutes * 60 * 1000;
    const now = new Date();

    if (now.getTime() - lastMessageAt.getTime() > expirationMs) {
      // Session expired - clean up storage
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }

    // Session is active if user has opened the chat OR has a valid sessionId
    return parsed.hasUserOpened === true || Boolean(parsed.sessionId && parsed.sessionId.length > 0);
  } catch {
    return false;
  }
};

// Helper function to check if widget should be visible based on dev mode
const shouldShowWidget = (config: ChatWidgetConfig): boolean => {
  // If dev mode is not configured or disabled, always show widget
  if (!config.devMode || !config.devMode.enabled) {
    return true;
  }

  // Check if there's an active session - if so, bypass devMode check
  // This allows returning visitors to see the chat even without devmode URL param
  if (hasActiveSession(config)) {
    return true;
  }

  // Dev mode is enabled and no active session - check URL parameter
  const urlParam = config.devMode.urlParam || 'devmode';
  const expectedValue = config.devMode.urlValue || 'on';

  // Parse URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const actualValue = urlParams.get(urlParam);

  // Show widget only if URL parameter matches expected value
  return actualValue === expectedValue;
};

// Chat Widget API
const ChatWidgetAPI = {
  init: (config: ChatWidgetConfig): ChatWidgetInstance => {
    const container = document.getElementById(config.containerId);

    if (!container) {
      throw new Error(`Container with id "${config.containerId}" not found`);
    }

    // Validate required config
    if (!config.apiUrl) {
      throw new Error('apiUrl is required in config');
    }

    // Check if widget should be visible based on dev mode
    const isVisible = shouldShowWidget(config);

    // Create React root
    const root = createRoot(container);

    // Render widget only if visible, otherwise render nothing
    if (isVisible) {
      root.render(
        <React.StrictMode>
          <ChatWidget config={config} />
        </React.StrictMode>
      );
    } else {
      // Render empty container when hidden
      root.render(null);
    }

    // Return instance with control methods
    return {
      destroy: () => {
        root.unmount();
      },
      update: (newConfig: Partial<ChatWidgetConfig>) => {
        const updatedConfig = { ...config, ...newConfig };
        const updatedVisible = shouldShowWidget(updatedConfig);

        if (updatedVisible) {
          root.render(
            <React.StrictMode>
              <ChatWidget config={updatedConfig} />
            </React.StrictMode>
          );
        } else {
          root.render(null);
        }
      }
    };
  }
};

// Attach to global namespace
if (typeof window !== 'undefined') {
  window.PospehWidgets.Chat = ChatWidgetAPI;
}

// Export for module systems
export default ChatWidgetAPI;
